import React, { useState, memo, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Formik, Form, useFormikContext } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import qs from 'query-string'
import Inputs from 'components/Inputs'
import { updatePasswordRequest, resetPasswordRequest, verifyTokenRequest } from 'state/profile'
import { selectAuthErrors, errorMessages } from 'state/error/authErrors'
import { useRedirect } from 'hooks'
import Text from 'particles/Text'
import Checkbox from 'particles/Checkbox'
import RectangleButton from 'particles/Button/RectangleButton'
import * as yup from 'yup'
import { AlertContentType } from 'particles/Alert'
import ReminderAlert from 'particles/Alert/ReminderAlert'
import ExpireLink from '../Shared/ExpireLink'
import styled from 'particles/themes/styled'
import { RegisterActions } from 'services/api/register'
import { selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'

const ErrorBoundary: React.FC<{ errors: Record<string, string> | null }> = ({ errors, children }) => {
    const [t] = useTranslation()
    const { setErrors } = useFormikContext<any>()

    const messages = useMemo(() => errorMessages(t, true), [t])

    useEffect(() => {
        if (errors) {
            const displayErrors = { ...errors }
            for (const key in displayErrors) {
                const errorCode = errors[key]
                const errorMessage = messages[errorCode]
                displayErrors[key] = errorMessage || errorCode
            }
            setErrors(displayErrors)
        }
    }, [errors, t])
    return <>{children}</>
}
interface AuthFormProps {
    email: string | null
    uid: string
    token: string
    isSendEmail: boolean
    onSubmitSuccessCallback: (isEmailExisted?: boolean, email?: string) => void
    isSignUp: boolean
    disableEmail: boolean
    blockType: {
        email: boolean
        passwords: boolean
        registerStep: null | number
    }
    onBack: () => void
}

const AuthForm: React.FC<AuthFormProps> = ({
    email,
    uid,
    token,
    isSendEmail,
    onSubmitSuccessCallback,
    isSignUp,
    disableEmail = false,
    blockType,
    onBack,
}) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const [showPWD, setShowPWD] = useState(false)
    const [showGeneralAlert, setShowGeneralAlert] = useState(false)
    const errors = useSelector(selectAuthErrors)
    const validationSchema = useMemo(
        () =>
            yup.object({
                email: yup
                    .string()
                    .email(t('errorMessage.validation.enterValidEmail', { email: 'name@example.com' }))
                    .test({
                        message: () => t('errorMessage.resetPassword.emptyEmail'),
                        test: function (value) {
                            if (blockType?.email) {
                                return value !== undefined && value !== null && value !== ''
                            }
                            return true
                        },
                    }),
                new_password1: yup
                    .string()
                    .min(8, t('errorMessage.password.characterLengthAndCombination'))
                    .matches(
                        /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d\S]{8,}$/,
                        t('errorMessage.password.characterLengthAndCombination'),
                    )
                    .test({
                        message: () => t('errorMessage.forgetPassword.emptyPassword'),
                        test: function (value) {
                            if (blockType?.passwords) {
                                return value !== undefined && value !== null && value !== ''
                            }
                            return true
                        },
                    })
                    .test({
                        message: () => t('errorMessage.signUp.passwordSimilarToEmail'),
                        test: function (value) {
                            const email = this.parent.email
                            if (value && email) {
                                const normalizedPassword = value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
                                const normalizedEmail = email
                                    .split('@')[0]
                                    .replace(/[^a-zA-Z0-9]/g, '')
                                    .toLowerCase()
                                return !normalizedPassword.includes(normalizedEmail)
                            }
                            return true
                        },
                    }),
                new_password2: yup
                    .string()
                    .test({
                        message: () => t('errorMessage.password.samePassword'),
                        test: function (value) {
                            const password = this.parent?.new_password1
                            if (!password || !value) {
                                return true
                            }
                            return value === password
                        },
                    })
                    .test({
                        message: () => t('errorMessage.forgetPassword.emptyReEnterPassword'),
                        test: function (value) {
                            if (blockType?.passwords) {
                                return value !== undefined && value !== null && value !== ''
                            }
                            return true
                        },
                    }),
            }),
        [blockType],
    )
    const isLoading = useSelector(
        selectRequestsAreLoading([
            STATE_KEYS.VerifyEmail,
            STATE_KEYS.SignUp,
            STATE_KEYS.UpdatePassword,
            STATE_KEYS.ResetPassword,
        ]),
    )
    return (
        <Formik
            initialValues={{
                uid,
                token,
                email: email ? email : '',
                new_password1: '',
                new_password2: '',
            }}
            enableReinitialize
            validateOnBlur={true}
            validationSchema={validationSchema}
            onSubmit={values => {
                if (isLoading) return
                blockType?.registerStep === 0
                    ? dispatch({
                          type: RegisterActions.VALIDATE_IS_EMAIL_EXISTED_REQUEST,
                          payload: {
                              email: values.email,
                              onSuccess: (isExist: boolean) => onSubmitSuccessCallback(isExist, values.email),
                              onError: () => setShowGeneralAlert(true),
                          },
                      })
                    : blockType.registerStep === 1
                      ? dispatch({
                            type: RegisterActions.SIGN_UP,
                            payload: {
                                email: values.email,
                                password1: values.new_password1,
                                password2: values.new_password2,
                                onSuccess: () => onSubmitSuccessCallback(true, values.email),
                                onError: () => setShowGeneralAlert(true),
                            },
                        })
                      : isSendEmail
                        ? dispatch(resetPasswordRequest({ email: values.email, onSuccess: onSubmitSuccessCallback }))
                        : dispatch(
                              updatePasswordRequest({
                                  ...values,
                                  onSuccess: onSubmitSuccessCallback,
                                  isSignUp: isSignUp,
                              }),
                          )
            }}
        >
            {({ resetForm }) => {
                return (
                    <Form>
                        {showGeneralAlert && (
                            <ReminderAlert
                                form="error"
                                title={t('errorMessage.general.somethingWentWrongTitle')}
                                subtext={t('errorMessage.signUp.refreshScreen')}
                                isClosable={false}
                                width="100%"
                            />
                        )}
                        <ErrorBoundary errors={errors}>
                            {blockType.email && (
                                <Inputs.TextField
                                    name="email"
                                    label={t('manageTeams.teamPage.memberEmailTableColumnLabel')}
                                    isReadOnly={disableEmail}
                                    width={'100%'}
                                    enableTrim
                                />
                            )}
                            {blockType.passwords && (
                                <>
                                    <Inputs.TextField
                                        type={showPWD ? 'text' : 'password'}
                                        name="new_password1"
                                        label={t('logIn.homepage.enterPassword')}
                                        enableTrim
                                        data-private
                                    />
                                    <Inputs.TextField
                                        type={showPWD ? 'text' : 'password'}
                                        name="new_password2"
                                        label={t('logIn.chooseNewPassword.reenterPassword')}
                                        enableTrim
                                        data-private
                                    />
                                    <Checkbox
                                        label={t('logIn.homepage.showPassword')}
                                        onChange={() => {
                                            setShowPWD(!showPWD)
                                        }}
                                        checkbox={showPWD ? 'selected' : 'notSelected'}
                                    />
                                </>
                            )}
                            <RectangleButton
                                type="submit"
                                isLoading={isLoading}
                                label={
                                    blockType?.registerStep === 0
                                        ? t('button.continue')
                                        : isSendEmail
                                          ? t('button.sendLink')
                                          : isSignUp || blockType?.registerStep === 1
                                            ? t('button.createAccount')
                                            : t('button.changePassword')
                                }
                                size="l"
                                isDisabled={isLoading}
                            />
                            {blockType?.registerStep === 1 && (
                                <RectangleButton
                                    type="button"
                                    label={t('campaign.template.back.button.text')}
                                    form="text"
                                    size="l"
                                    onClick={() => {
                                        onBack()
                                        resetForm()
                                    }}
                                />
                            )}
                        </ErrorBoundary>
                    </Form>
                )
            }}
        </Formik>
    )
}

const SignUpAndReset: React.FC<{ className?: string }> = memo(({ className }) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const [alert, setAlert] = useState<{
        show: boolean
        configType: AlertContentType
        successTitle: string
        successSubtext: string
        isRedirectToLogin: boolean
        reminderAction?: {
            label: string
            callback: () => void
        }
        hideButtonSet: boolean
    }>({
        show: false,
        configType: 'positive',
        successTitle: '',
        successSubtext: '',
        isRedirectToLogin: true,
        hideButtonSet: false,
    })
    const [tokenValidateResult, setTokenValidateResult] = useState<{
        email: string | null
        isExpired: boolean | null
    }>({
        email: null,
        isExpired: false,
    })
    const [blockType, setBlockType] = useState<{ email: boolean; passwords: boolean; registerStep: null | number }>({
        email: true,
        passwords: true,
        registerStep: null,
    })
    const location = useLocation()
    const { redirectToLogin } = useRedirect()
    const params = qs.parse(location.search)
    const { uid, token } = params
    const isRegister = location?.pathname === '/register'
    const isSignUp = location?.pathname === '/sign-up'
    const isSendEmail = location?.pathname === '/reset-password'

    const registerCallback = (isEmailExisted?: boolean, email?: string) => {
        if (isEmailExisted !== undefined && isEmailExisted === false) {
            setBlockType({ ...blockType, email: false, passwords: true, registerStep: 1 })
        } else {
            setAlert({
                ...alert,
                show: true,
                configType: 'info',
                successTitle: t('logIn.signUp.activateEmailTitle', { 'user email': email }),
                successSubtext: t('logIn.signUp.activateEmailSubtitle'),
                reminderAction: {
                    label: t('button.sendLinkAgain'),
                    callback: () => dispatch({ type: RegisterActions.SEND_INVITE, payload: { email: email } }),
                },
                isRedirectToLogin: false,
                hideButtonSet: false,
            })
        }
    }

    useEffect(() => {
        switch (location?.pathname) {
            case '/reset-password':
                setBlockType({ ...blockType, passwords: false })
                break
            case '/register':
                setBlockType({ ...blockType, passwords: false, registerStep: 0 })
                break
            default:
                setBlockType({ ...blockType, email: true, passwords: true })
        }
    }, [location?.pathname])

    useEffect(() => {
        if (isSendEmail || isRegister) {
            return
        } else if (typeof uid === 'string' && typeof token === 'string') {
            dispatch(
                verifyTokenRequest({
                    uid: uid,
                    token: token,
                    onSuccess: (email: string) => {
                        setTokenValidateResult({ ...tokenValidateResult, email: email, isExpired: false })
                    },
                    onError: () => {
                        setTokenValidateResult({ ...tokenValidateResult, isExpired: true })
                    },
                }),
            )
        } else {
            redirectToLogin()
        }
    }, [uid, token, isSendEmail, isRegister])

    if (tokenValidateResult?.isExpired)
        return (
            <ExpireLink
                subtext={
                    isSignUp
                        ? [
                              t('logIn.linkExpired.invitationExpiredSubtext.firstLine'),
                              t('logIn.linkExpired.invitationExpiredSubtext.secondLine'),
                          ]
                        : [t('logIn.linkExpiredSubtext.firstLine'), t('logIn.linkExpiredSubtext.secondLine')]
                }
            />
        )

    return (
        <div className={className}>
            <div className="block">
                <Text scale="H2">
                    {isRegister
                        ? t('logIn.signUp.signUpTitle')
                        : isSendEmail
                          ? t('logIn.resetPasswordHeader')
                          : isSignUp
                            ? t('logIn.welcomeHeader')
                            : t('logIn.forgetPasswordHeader')}
                </Text>
                {isSendEmail && <Text>{t('logIn.resetPasswordSubtext')}</Text>}
            </div>
            <div className="block">
                {alert.show ? (
                    <div className="auth-form">
                        <ReminderAlert
                            form={alert?.configType}
                            title={alert?.successTitle}
                            subtext={alert?.successSubtext}
                            action={alert?.reminderAction}
                            isClosable={false}
                            width="100%"
                        />
                        {alert.isRedirectToLogin && (
                            <RectangleButton label={t('button.returnToLogIn')} size="l" onClick={redirectToLogin} />
                        )}
                    </div>
                ) : (
                    <AuthForm
                        uid={uid as string}
                        token={token as string}
                        isSendEmail={isSendEmail}
                        onSubmitSuccessCallback={(isEmailExisted?: boolean, email?: string) => {
                            if (isRegister) {
                                registerCallback(isEmailExisted, email)
                            } else if (!isSignUp) {
                                setAlert({
                                    ...alert,
                                    show: true,
                                    successTitle: isSendEmail
                                        ? t('successMessage.emailSentTitle')
                                        : t('successMessage.passwordReset'),
                                    successSubtext: isSendEmail
                                        ? t('successMessage.emailSentSubtext')
                                        : t('successMessage.passwordResetSubtext'),
                                    isRedirectToLogin: true,
                                    hideButtonSet: true,
                                })
                            }
                        }}
                        onBack={() => {
                            setBlockType({ ...blockType, email: true, passwords: false, registerStep: 0 })
                        }}
                        email={tokenValidateResult?.email}
                        isSignUp={isSignUp}
                        disableEmail={!isRegister && !isSendEmail}
                        blockType={blockType}
                    />
                )}

                {!alert.hideButtonSet && (isSignUp || isSendEmail || isRegister) && (
                    <div className="login-link">
                        {(isSignUp || isRegister) && <Text>{t('logIn.homepage.alreadyHaveAccount')}</Text>}
                        <RectangleButton
                            className={isSendEmail ? 'fill' : ''}
                            form="text"
                            label={isSignUp || isRegister ? t('button.logIn') : t('button.returnToLogIn')}
                            size="s"
                            color="neutral"
                            onClick={redirectToLogin}
                        />
                    </div>
                )}
            </div>
            <Text scale="caption400" className="copyright">
                {t('logIn.partipostCopyrightFooter')}
            </Text>
        </div>
    )
})

SignUpAndReset.displayName = 'SignUpAndReset'
export default styled(SignUpAndReset)`
    max-width: 520px;
    width: 520px;
    min-width: 343px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: fit-content;
    max-height: 74vh;
    overflow-y: auto;
    gap: ${({ theme }) => theme.spacing[600]};
    background-color: ${({ theme }) => theme.colors.surface.primary};
    padding: 80px 60px;
    box-sizing: border-box;
    @media screen and (max-width: 400px) {
        width: 343px;
        justify-content: flex-start;
    }
    .block {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[200]};
    }
    form,
    .auth-form {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[400]};
    }
    .copyright {
        align-self: center;
    }
    .login-link {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        align-self: center;
        gap: ${({ theme }) => theme.spacing[300]};
        .fill {
            width: 100%;
        }
    }
`
